import React, { useState, useEffect } from 'react';
import { Palette, Type, Circle, Square, Layers, X, RotateCcw } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../ui/dialog';

const DEFAULT_STYLE = {
  table_fill_color: '#ffffff',
  table_stroke_color: '#d1d5db',
  table_stroke_width: 2,
  table_name_font_size: 14,
  table_name_font_color: '#374151',
  highlighted_table_color: '#a3b6a0',
  highlighted_font_size: 16,
  highlighted_font_color: '#ffffff',
  venue_element_color: '#e5e7eb',
  venue_element_stroke: '#9ca3af',
  venue_element_font_size: 12,
  venue_element_font_color: '#6b7280',
  custom_piece_fill_color: '#f0eee9',
  custom_piece_stroke_color: '#1e40af',
  seat_color: '#9ca3af',
  background_color: '#f8fafc',
};

const ColorInput = ({ label, value, onChange, id }) => (
  <div className="flex items-center justify-between gap-3">
    <Label htmlFor={id} className="text-sm text-gray-600 whitespace-nowrap">{label}</Label>
    <div className="flex items-center gap-2">
      <input
        type="color"
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-8 h-8 rounded cursor-pointer border border-gray-300 shadow-sm"
      />
      <Input
        type="text"
        value={value}
        onChange={(e) => {
          let val = e.target.value;
          // Auto-add # if missing
          if (val && !val.startsWith('#') && /^[0-9A-Fa-f]+$/.test(val)) {
            val = '#' + val;
          }
          onChange(val);
        }}
        placeholder="#000000"
        className="w-24 h-8 px-2 text-xs font-mono uppercase"
        maxLength={7}
      />
    </div>
  </div>
);

const FloorPlanStylePanel = ({ style = {}, onStyleChange, disabled = false, adminDarkMode = true }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [originalStyle, setOriginalStyle] = useState(null);
  const [currentStyle, setCurrentStyle] = useState({ ...DEFAULT_STYLE, ...style });

  // Store original style when dialog opens
  useEffect(() => {
    if (isOpen) {
      setOriginalStyle({ ...DEFAULT_STYLE, ...style });
      setCurrentStyle({ ...DEFAULT_STYLE, ...style });
    }
  }, [isOpen, style]);

  const handleChange = (key, value) => {
    const newStyle = { ...currentStyle, [key]: value };
    setCurrentStyle(newStyle);
    onStyleChange(newStyle); // Live preview
  };

  const handleReset = () => {
    // Reset removes is_custom flag so theme colors take priority again
    const resetStyle = { ...DEFAULT_STYLE };
    delete resetStyle.is_custom;
    setCurrentStyle(resetStyle);
    onStyleChange(resetStyle);
  };

  const handleCancel = () => {
    // Revert to original style
    if (originalStyle) {
      onStyleChange(originalStyle);
    }
    setIsOpen(false);
  };

  const handleSave = () => {
    // Style is already saved via live preview, just close
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          className={adminDarkMode ? 'gap-2 border-slate-700 text-slate-300 hover:bg-slate-800' : 'gap-2 border-slate-200 text-slate-600 hover:bg-slate-100'}
          disabled={disabled}
          data-testid="floor-plan-style-btn"
        >
          <Palette className="w-4 h-4" />
          Guest View Style
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Palette className="w-5 h-5" />
            Guest View Style
          </DialogTitle>
          <p className="text-sm text-gray-500 mt-1">
            Customize how the floor plan looks to your guests
          </p>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Tables Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Square className="w-4 h-4" />
              Tables
            </div>
            <div className="pl-6 space-y-3">
              <ColorInput
                label="Fill Color"
                value={currentStyle.table_fill_color}
                onChange={(v) => handleChange('table_fill_color', v)}
                id="table-fill"
              />
              <ColorInput
                label="Border Color"
                value={currentStyle.table_stroke_color}
                onChange={(v) => handleChange('table_stroke_color', v)}
                id="table-stroke"
              />
              <div className="flex items-center justify-between gap-3">
                <Label className="text-sm text-gray-600">Border Width</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={currentStyle.table_stroke_width}
                    onChange={(e) => handleChange('table_stroke_width', Math.max(0, Math.min(10, parseInt(e.target.value) || 0)))}
                    className="w-20 h-8 text-sm"
                    min={0}
                    max={10}
                  />
                  <span className="text-xs text-gray-500">px</span>
                </div>
              </div>
            </div>
          </div>

          {/* Highlighted Table Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Square className="w-4 h-4 text-yellow-500" />
              Highlighted Table (Guest's Table)
            </div>
            <div className="pl-6 space-y-3">
              <ColorInput
                label="Background"
                value={currentStyle.highlighted_table_color}
                onChange={(v) => handleChange('highlighted_table_color', v)}
                id="highlighted-table"
              />
              <div className="flex items-center justify-between gap-3">
                <Label className="text-sm text-gray-600">Font Size</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={currentStyle.highlighted_font_size}
                    onChange={(e) => handleChange('highlighted_font_size', Math.max(8, Math.min(32, parseInt(e.target.value) || 16)))}
                    className="w-20 h-8 text-sm"
                    min={8}
                    max={32}
                  />
                  <span className="text-xs text-gray-500">px</span>
                </div>
              </div>
              <ColorInput
                label="Font Color"
                value={currentStyle.highlighted_font_color}
                onChange={(v) => handleChange('highlighted_font_color', v)}
                id="highlighted-font"
              />
            </div>
          </div>

          {/* Table Names Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Type className="w-4 h-4" />
              Table Names
            </div>
            <div className="pl-6 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <Label className="text-sm text-gray-600">Font Size</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={currentStyle.table_name_font_size}
                    onChange={(e) => handleChange('table_name_font_size', Math.max(8, Math.min(32, parseInt(e.target.value) || 14)))}
                    className="w-20 h-8 text-sm"
                    min={8}
                    max={32}
                  />
                  <span className="text-xs text-gray-500">px</span>
                </div>
              </div>
              <ColorInput
                label="Font Color"
                value={currentStyle.table_name_font_color}
                onChange={(v) => handleChange('table_name_font_color', v)}
                id="table-name-font"
              />
            </div>
          </div>

          {/* Seats Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Circle className="w-4 h-4" />
              Seats
            </div>
            <div className="pl-6 space-y-3">
              <ColorInput
                label="Seat Color"
                value={currentStyle.seat_color}
                onChange={(v) => handleChange('seat_color', v)}
                id="seat-color"
              />
            </div>
          </div>

          {/* Venue Elements Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Layers className="w-4 h-4" />
              Venue Elements
            </div>
            <div className="pl-6 space-y-3">
              <ColorInput
                label="Fill Color"
                value={currentStyle.venue_element_color}
                onChange={(v) => handleChange('venue_element_color', v)}
                id="venue-fill"
              />
              <ColorInput
                label="Border Color"
                value={currentStyle.venue_element_stroke}
                onChange={(v) => handleChange('venue_element_stroke', v)}
                id="venue-stroke"
              />
              <div className="flex items-center justify-between gap-3">
                <Label className="text-sm text-gray-600">Font Size</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={currentStyle.venue_element_font_size}
                    onChange={(e) => handleChange('venue_element_font_size', Math.max(8, Math.min(32, parseInt(e.target.value) || 12)))}
                    className="w-20 h-8 text-sm"
                    min={8}
                    max={32}
                  />
                  <span className="text-xs text-gray-500">px</span>
                </div>
              </div>
              <ColorInput
                label="Font Color"
                value={currentStyle.venue_element_font_color}
                onChange={(v) => handleChange('venue_element_font_color', v)}
                id="venue-font-color"
              />
            </div>
          </div>

          {/* Custom Pieces Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Square className="w-4 h-4" />
              Custom Pieces
            </div>
            <div className="pl-6 space-y-3">
              <ColorInput
                label="Fill Color"
                value={currentStyle.custom_piece_fill_color}
                onChange={(v) => handleChange('custom_piece_fill_color', v)}
                id="piece-fill"
              />
              <ColorInput
                label="Border Color"
                value={currentStyle.custom_piece_stroke_color}
                onChange={(v) => handleChange('custom_piece_stroke_color', v)}
                id="piece-stroke"
              />
            </div>
          </div>

          {/* Background Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Square className="w-4 h-4 fill-current" />
              Background
            </div>
            <div className="pl-6 space-y-3">
              <ColorInput
                label="Background"
                value={currentStyle.background_color}
                onChange={(v) => handleChange('background_color', v)}
                id="background"
              />
            </div>
          </div>
        </div>

        <DialogFooter className="flex gap-2 pt-4 border-t">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleReset}
            className="text-gray-500 hover:text-gray-700"
          >
            <RotateCcw className="w-4 h-4 mr-1" />
            Reset
          </Button>
          <div className="flex-1" />
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button 
            size="sm" 
            onClick={handleSave}
            className="bg-yellow-500 hover:bg-yellow-600 text-slate-900"
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FloorPlanStylePanel;
export { DEFAULT_STYLE };
